import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, InputNumber, Button, message, Alert, Spin, Typography, Divider } from 'antd';
import { configureKpiZohoMapping, getKpiZohoMapping, syncKpiWithZoho, getKpiSyncHistory, getZohoConfig } from '../../api/zoho-crm';

const { Option } = Select;
const { Text, Title } = Typography;

const ZohoSyncModal = ({ visible, onClose, kpiId, kpiName, token, onSyncSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [existingMapping, setExistingMapping] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [lastSyncResult, setLastSyncResult] = useState(null);

  useEffect(() => {
    if (visible && kpiId) {
      loadExistingMapping();
      loadSyncHistory();
      checkZohoActive();
    }
  }, [visible, kpiId]);

  const loadExistingMapping = async () => {
    try {
      const response = await getKpiZohoMapping(kpiId, token);
      if (response.data) {
        setExistingMapping(response.data);
        form.setFieldsValue({
          syncType: response.data.syncType,
          dateField: response.data.leadCriteria.dateField,
          dateRange: response.data.leadCriteria.dateRange,
          status: response.data.leadCriteria.status,
          source: response.data.leadCriteria.source,
          sumField: response.data.sumField,
        });
      }
    } catch (error) {
      console.log('No existing mapping found');
    }
  };

  const loadSyncHistory = async () => {
    try {
      const response = await getKpiSyncHistory(kpiId, token);
      setSyncHistory(response.data.slice(0, 3)); // Show last 3 syncs
    } catch (error) {
      console.log('No sync history found');
    }
  };

  const handleSaveMapping = async (values) => {
    setLoading(true);
    try {
      const mappingData = {
        leadCriteria: {
          dateField: values.dateField,
          dateRange: values.dateRange,
          status: values.status,
          source: values.source,
        },
        syncType: values.syncType,
        sumField: values.sumField,
      };

      await configureKpiZohoMapping(kpiId, mappingData, token);
      message.success('Zoho mapping configured successfully!');
      setExistingMapping({ ...mappingData, kpiId });
    } catch (error) {
      message.error('Failed to save mapping: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncLoading(true);
    try {
      const response = await syncKpiWithZoho(kpiId, token);
      if (response.success) {
        message.success(response.message);
        setLastSyncResult(response.data);
        loadSyncHistory(); // Refresh history
        if (onSyncSuccess) {
          onSyncSuccess(response.data);
        }
      } else {
        message.error(response.message || 'Sync failed');
      }
    } catch (error) {
      message.error('Sync failed: ' + (error.message || 'Unknown error'));
    } finally {
      setSyncLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setLastSyncResult(null);
    onClose();
  };

  const [isZohoActive, setIsZohoActive] = useState(true);
  async function checkZohoActive() {
    try {
      const cfg = await getZohoConfig(token);
      setIsZohoActive(!!cfg?.data?.isActive);
    } catch (e) {
      setIsZohoActive(true);
    }
  }

  const statusOptions = [
    'Not Contacted', 'Contacted', 'Qualified', 'Unqualified', 
    'Junk Lead', 'Pre-Qualified', 'Not Qualified'
  ];

  const sourceOptions = [
    'Advertisement', 'Cold Call', 'Employee Referral', 'External Referral',
    'Online Store', 'Partner', 'Public Relations', 'Sales Email Alias',
    'Seminar Partner', 'Trade Show', 'Web Download', 'Web Research',
    'Chat', 'Phone', 'Webinar'
  ];

  return (
    <Modal
      title={`Zoho CRM Sync - ${kpiName}`}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={700}
    >
      {!isZohoActive && (
        <Alert type="warning" message="Zoho CRM is disabled. Enable it in Settings > General > Zoho CRM to sync." showIcon style={{ marginBottom: 16 }} />
      )}
      <Alert
        message="Configure how this KPI should sync with Zoho CRM leads"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {lastSyncResult && (
        <Alert
          message="Sync Successful!"
          description={`Updated from ${lastSyncResult.oldValue} to ${lastSyncResult.newValue}. Processed ${lastSyncResult.leadsProcessed} leads.`}
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSaveMapping}
        initialValues={{
          syncType: 'count',
          dateField: 'Created_Time',
          dateRange: 30,
        }}
      >
        <Form.Item
          label="Sync Type"
          name="syncType"
          rules={[{ required: true, message: 'Please select sync type' }]}
        >
          <Select placeholder="Select how to calculate the value">
            <Option value="count">Count Leads</Option>
            <Option value="sum">Sum Field Value</Option>
          </Select>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.syncType !== currentValues.syncType}
        >
          {({ getFieldValue }) =>
            getFieldValue('syncType') === 'sum' ? (
              <Form.Item
                label="Sum Field"
                name="sumField"
                rules={[{ required: true, message: 'Please enter field to sum' }]}
              >
                <Select placeholder="Select field to sum">
                  <Option value="Annual_Revenue">Annual Revenue</Option>
                  <Option value="No_of_Employees">Number of Employees</Option>
                </Select>
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item
          label="Date Field"
          name="dateField"
          rules={[{ required: true, message: 'Please select date field' }]}
        >
          <Select placeholder="Select date field for filtering">
            <Option value="Created_Time">Created Date</Option>
            <Option value="Modified_Time">Modified Date</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Date Range (Days)"
          name="dateRange"
          rules={[{ required: true, message: 'Please enter date range' }]}
        >
          <InputNumber 
            min={1} 
            max={365} 
            placeholder="Number of days to look back"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          label="Lead Status Filter (Optional)"
          name="status"
        >
          <Select 
            mode="multiple" 
            placeholder="Select lead statuses to include"
            allowClear
          >
            {statusOptions.map(status => (
              <Option key={status} value={status}>{status}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Lead Source Filter (Optional)"
          name="source"
        >
          <Select 
            mode="multiple" 
            placeholder="Select lead sources to include"
            allowClear
          >
            {sourceOptions.map(source => (
              <Option key={source} value={source}>{source}</Option>
            ))}
          </Select>
        </Form.Item>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Button onClick={handleCancel}>
              Cancel
            </Button>
            <Button 
              type="default" 
              htmlType="submit" 
              loading={loading}
              style={{ marginLeft: 8 }}
            >
              Save Mapping
            </Button>
          </div>
          <Button 
            type="primary" 
            onClick={handleSync}
            loading={syncLoading}
            disabled={!existingMapping && !loading || !isZohoActive}
          >
            {syncLoading ? 'Syncing...' : 'Sync Now'}
          </Button>
        </div>
      </Form>

      {syncHistory.length > 0 && (
        <>
          <Divider />
          <Title level={5}>Recent Sync History</Title>
          {syncHistory.map((sync, index) => (
            <div key={sync.id} style={{ 
              padding: 8, 
              marginBottom: 8, 
              backgroundColor: sync.status === 'success' ? '#f6ffed' : '#fff2f0',
              border: `1px solid ${sync.status === 'success' ? '#b7eb8f' : '#ffccc7'}`,
              borderRadius: 4 
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong>{sync.status === 'success' ? '✅ Success' : '❌ Failed'}</Text>
                <Text type="secondary">{new Date(sync.createdOn).toLocaleString()}</Text>
              </div>
              {sync.status === 'success' ? (
                <Text>Processed {sync.leadsProcessed} leads. Value: {sync.oldValue} → {sync.newValue}</Text>
              ) : (
                <Text type="danger">{sync.errorMessage}</Text>
              )}
            </div>
          ))}
        </>
      )}
    </Modal>
  );
};

export default ZohoSyncModal;